import React, { useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { createEvent } from 'wasp/client/operations';
import { useAuth } from 'wasp/client/auth';
import { 
  Calendar,
  Clock,
  MapPin,
  Video,
  Upload,
  Save,
  AlertCircle,
  HelpCircle,
  Music,
  GraduationCap,
  Briefcase,
  Coffee
} from 'lucide-react';
import { DashboardLayout } from '../../client/components/Sidebar';
import { useLocationContext } from '../../client/contexts/LocationContext';
import { resolveLocationSlug, locationSectionPath } from '../../client/lib/locationPaths';

// Tooltip component (reusable)
const Tooltip = ({ children, content }: { children: React.ReactNode; content: string }) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        className="cursor-help"
      >
        {children}
      </div>
      {isVisible && (
        <div className="absolute z-10 px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64">
          {content}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );
};

interface EventFormData {
  title: string;
  description: string;
  eventType: string;
  startTime: string;
  endTime: string;
  timezone: string;
  venueName: string;
  venueAddress: string;
  isVirtual: boolean;
  meetingUrl: string;
  coverImage: string;
  maxAttendees: string;
  requiresRegistration: boolean;
  visibility: 'PUBLIC' | 'VERIFIED' | 'SUBSCRIBER';
}

const EVENT_TYPES = [
  { value: 'workshop', label: 'Workshop', icon: GraduationCap },
  { value: 'meeting', label: 'Meeting', icon: Briefcase },
  { value: 'cultural', label: 'Cultural', icon: Music },
  { value: 'business', label: 'Business', icon: Briefcase },
  { value: 'social', label: 'Social', icon: Coffee },
  { value: 'educational', label: 'Educational', icon: GraduationCap }
];


export default function AddEventPage() {
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const params = useParams<{ slug?: string }>();
  const routerLocation = useLocation();
  const locSlug = resolveLocationSlug(routerLocation.pathname, params.slug, selectedLocation);
  const eventsListPath = locationSectionPath(locSlug, 'events');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  const [formData, setFormData] = useState<EventFormData>({
    title: '',
    description: '',
    eventType: 'workshop',
    startTime: '',
    endTime: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    venueName: '',
    venueAddress: '',
    isVirtual: false,
    meetingUrl: '',
    coverImage: '',
    maxAttendees: '',
    requiresRegistration: true,
    visibility: 'VERIFIED'
  });

  const handleChange = (field: keyof EventFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Event title is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Event description is required';
    }

    if (!formData.startTime) {
      newErrors.startTime = 'Start time is required';
    }

    if (!formData.endTime) {
      newErrors.endTime = 'End time is required';
    }

    if (formData.startTime && formData.endTime) {
      const startDate = new Date(formData.startTime);
      const endDate = new Date(formData.endTime);
      if (endDate <= startDate) {
        newErrors.endTime = 'End time must be after start time';
      }
    }

    if (formData.isVirtual && !formData.meetingUrl.trim()) {
      newErrors.meetingUrl = 'Meeting URL is required for virtual events';
    }

    if (!formData.isVirtual && !formData.venueName.trim()) {
      newErrors.venueName = 'Venue name is required for in-person events';
    }

    if (formData.maxAttendees && isNaN(Number(formData.maxAttendees))) {
      newErrors.maxAttendees = 'Maximum attendees must be a number';
    }

    if (!selectedLocation?.id) {
      newErrors.location = 'No location selected';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!selectedLocation?.id) {
      setErrors({ location: 'No location selected' });
      return;
    }

    setIsSubmitting(true);

    try {
      const eventData = {
        locationId: selectedLocation.id,
        title: formData.title.trim(),
        description: formData.description.trim(),
        eventType: formData.eventType,
        startTime: formData.startTime,
        endTime: formData.endTime,
        timezone: formData.timezone,
        venueName: formData.venueName.trim(),
        venueAddress: formData.venueAddress.trim(),
        isVirtual: formData.isVirtual,
        meetingUrl: formData.meetingUrl.trim(),
        coverImage: formData.coverImage.trim(),
        maxAttendees: formData.maxAttendees ? Number(formData.maxAttendees) : undefined,
        requiresRegistration: formData.requiresRegistration,
        visibility: formData.visibility
      };

      await createEvent(eventData);

      navigate(eventsListPath);
    } catch (error: any) {
      console.error('Error creating event:', error);
      setErrors({ submit: error.message || 'Failed to create event' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Authentication and location checks
  if (!user) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Authentication Required</h2>
            <p className="text-gray-600">You must be logged in to create events.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!selectedLocation) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">No Location Selected</h2>
            <p className="text-gray-600">Please select a location to create an event.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="flex h-full">
        {/* Main Content Area - Takes full width excluding left sidebar */}
        <div className="flex-1 bg-card">
          <div className="flex h-full">
            {/* Form Content - Left side */}
            <div className="flex-1 p-6 pb-32 lg:pb-6">
              {/* Header */}
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <Calendar className="w-8 h-8 text-blue-600" />
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">Add Event</h1>
                    <p className="text-gray-600">Location: {selectedLocation?.name || 'No location selected'}</p>
                  </div>
                </div>
              </div>

              <form id="event-form" onSubmit={handleSubmit}>
                {/* Full-width sections */}
                <div className="space-y-6 mb-6">
                  {/* Primary Input Field */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">What event are you organizing?</h3>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => handleChange('title', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${
                        errors.title ? 'border-red-500 bg-red-50' : 'border-input'
                      }`}
                      placeholder="e.g., Weekly Team Meeting, Yoga Class, Community Workshop"
                      required
                    />
                    {errors.title && (
                      <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.title}
                      </p>
                    )}
                  </div>

                  {/* Description Field */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Description</h3>
                    <textarea
                      value={formData.description}
                      onChange={(e) => handleChange('description', e.target.value)}
                      rows={4}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${
                        errors.description ? 'border-red-500 bg-red-50' : 'border-input'
                      }`}
                      placeholder="Provide detailed description of the event, agenda, what to expect, materials needed..."
                      required
                    />
                    {errors.description && (
                      <p className="text-red-600 text-sm mt-1 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.description}
                      </p>
                    )}
                    <p className="text-sm text-gray-500 mt-2">{formData.description.length}/1000 characters</p>
                  </div>
                </div>

                {/* Two-column layout for form fields */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Left Column */}
                  <div className="space-y-6">
                    {/* Date and Time Fields */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">Start Date & Time</label>
                        <input
                          type="datetime-local"
                          value={formData.startTime}
                          onChange={(e) => handleChange('startTime', e.target.value)}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${
                            errors.startTime ? 'border-red-500 bg-red-50' : 'border-input'
                          }`}
                          required
                        />
                        {errors.startTime && (
                          <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                            <AlertCircle className="w-4 h-4" />
                            {errors.startTime}
                          </p>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">End Date & Time</label>
                        <input
                          type="datetime-local"
                          value={formData.endTime}
                          onChange={(e) => handleChange('endTime', e.target.value)}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${
                            errors.endTime ? 'border-red-500 bg-red-50' : 'border-input'
                          }`}
                          required
                        />
                        {errors.endTime && (
                          <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                            <AlertCircle className="w-4 h-4" />
                            {errors.endTime}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Event Type and Location */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">Event Type</label>
                        <select
                          value={formData.eventType}
                          onChange={(e) => handleChange('eventType', e.target.value)}
                          className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                        >
                          {EVENT_TYPES.map(type => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">Event Location</label>
                        <div className="flex items-center gap-4">
                          <label className="flex items-center space-x-3 cursor-pointer">
                            <input
                              type="radio"
                              name="eventLocation"
                              checked={!formData.isVirtual}
                              onChange={() => handleChange('isVirtual', false)}
                              className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                            />
                            <div className="flex items-center gap-2">
                              <div className="font-medium text-gray-900">In-Person</div>
                              <MapPin className="w-4 h-4 text-gray-400" />
                            </div>
                          </label>
                          <label className="flex items-center space-x-3 cursor-pointer">
                            <input
                              type="radio"
                              name="eventLocation"
                              checked={formData.isVirtual}
                              onChange={() => handleChange('isVirtual', true)}
                              className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                            />
                            <div className="flex items-center gap-2">
                              <div className="font-medium text-gray-900">Virtual</div>
                              <Video className="w-4 h-4 text-gray-400" />
                            </div>
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Conditional fields based on virtual/in-person */}
                    {formData.isVirtual ? (
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          <div className="flex items-center gap-2">
                            <span>Meeting URL</span>
                            <Tooltip content="Provide the meeting link for virtual events">
                              <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                            </Tooltip>
                          </div>
                        </label>
                        <input
                          type="url"
                          value={formData.meetingUrl}
                          onChange={(e) => handleChange('meetingUrl', e.target.value)}
                          placeholder="https://zoom.us/j/123456789 or meeting link"
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${
                            errors.meetingUrl ? 'border-red-500 bg-red-50' : 'border-input'
                          }`}
                        />
                        {errors.meetingUrl && (
                          <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                            <AlertCircle className="w-4 h-4" />
                            {errors.meetingUrl}
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">Venue Name</label>
                          <input
                            type="text"
                            value={formData.venueName}
                            onChange={(e) => handleChange('venueName', e.target.value)}
                            placeholder="e.g., Conference Room A, Main Hall, Community Center"
                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${
                              errors.venueName ? 'border-red-500 bg-red-50' : 'border-input'
                            }`}
                          />
                          {errors.venueName && (
                            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                              <AlertCircle className="w-4 h-4" />
                              {errors.venueName}
                            </p>
                          )}
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">Address (Optional)</label>
                          <input
                            type="text"
                            value={formData.venueAddress}
                            onChange={(e) => handleChange('venueAddress', e.target.value)}
                            placeholder="Full address if different from location"
                            className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                          />
                        </div>
                      </div>
                    )}

                    {/* File Upload Section */}
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Event Image</h3>
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                        <div className="text-center">
                          <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-gray-500 mb-2">Upload an image to support your event</p>
                          <p className="text-sm text-gray-400">PNG, JPG up to 10MB</p>
                        </div>
                      </div>
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-foreground mb-2">Cover Image URL (Optional)</label>
                        <input
                          type="url"
                          value={formData.coverImage}
                          onChange={(e) => handleChange('coverImage', e.target.value)}
                          placeholder="https://example.com/image.jpg"
                          className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-6">
                    {/* Maximum Attendees Field */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <div className="flex items-center gap-2">
                          <span>Maximum Attendees</span>
                          <Tooltip content="Leave empty for unlimited attendees">
                            <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                          </Tooltip>
                        </div>
                      </label>
                      <input
                        type="number"
                        value={formData.maxAttendees}
                        onChange={(e) => handleChange('maxAttendees', e.target.value)}
                        placeholder="Leave empty for unlimited"
                        min="1"
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${
                          errors.maxAttendees ? 'border-red-500 bg-red-50' : 'border-input'
                        }`}
                      />
                      {errors.maxAttendees && (
                        <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          {errors.maxAttendees}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Error message */}
                {errors.submit && (
                  <div className="mt-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                    {errors.submit}
                  </div>
                )}
              </form>
            </div>

            {/* Right Sidebar - Anchored to the right */}
            <div className="hidden lg:flex w-80 border-l border-border flex-col">
              {/* Action Buttons */}
              <div className="p-6 border-b border-border">
                <div className="flex flex-col gap-3">
                  <button
                    type="submit"
                    form="event-form"
                    onClick={handleSubmit}
                    disabled={isSubmitting || !formData.title.trim()}
                    className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        Submit
                      </>
                    )}
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => navigate(eventsListPath)}
                    className="w-full px-4 py-3 text-foreground bg-card border border-input rounded-lg hover:bg-muted transition-colors font-medium"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                </div>
              </div>

              {/* Approval Notice */}
              <div className="p-6 border-b border-border">
                <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>All postings go through an approval process before being published.</span>
                </div>
              </div>

              {/* Additional Options */}
              <div className="p-6 border-b border-border">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Options</h3>
                <div className="space-y-3">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.requiresRegistration}
                      onChange={(e) => handleChange('requiresRegistration', e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <div className="flex items-center gap-2">
                      <div className="font-medium text-gray-900">Require Registration</div>
                      <Tooltip content="Attendees must register to attend this event">
                        <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                      </Tooltip>
                    </div>
                  </label>
                </div>
              </div>

              {/* Additional Fields Section */}
              <div className="p-6 flex-1">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Event Details</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Timezone</label>
                    <input
                      type="text"
                      value={formData.timezone}
                      onChange={(e) => handleChange('timezone', e.target.value)}
                      className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                      readOnly
                    />
                    <p className="mt-1 text-xs text-gray-500">Automatically detected from your browser</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Bottom Action Bar */}
      <div className="fixed bottom-14 left-0 right-0 z-10 lg:hidden bg-card border-t border-border px-4 py-3 shadow-lg">
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => navigate(eventsListPath)}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2.5 text-foreground bg-card border border-input rounded-lg hover:bg-muted transition-colors font-medium text-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="event-form"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-sm flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
            ) : 'Post'}
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
}


